import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { RiskAssessmentResultControlType } from '@risksmart-app/domain/src/types/consts/risk-assessment-result-control-type';
import type { InsertRiskAssessmentResultsMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  InsertRiskAssessmentResultsDocument,
  namedOperations,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export type InsertRiskAssessmentResultInput = {
  RiskIds: string[];
  ControlType: RiskAssessmentResultControlType;
  Rating?: number | null;
  Likelihood?: number | null;
  Impact?: number | null;
  Impacts?: { Label: string; Value: number }[] | null;
  AssessmentId?: string | null;
  CustomAttributeData?: CustomAttributeData | null;
  TestDate?: string | null;
  Rationale?: string | null;
};

export const useInsertRiskAssessmentResult = createMutationHook({
  name: 'insertRiskAssessmentResult',
  isIdempotentCreate: true,
  mutationOptions: (trpc, onSuccess, requestContext) =>
    trpc.frontend.riskAssessmentResult.insert.mutationOptions({
      onSuccess,
      trpc: { context: requestContext },
    }),
  mapInput: (input: InsertRiskAssessmentResultInput) => input,
  invalidate: (trpc) => [
    // COI-889: router-level key — the hand-listed set missed
    // `assessmentResultCount`, the Findings left-nav badge.
    trpc.frontend.assessment.pathKey(),
    trpc.frontend.risk.register.queryKey(),
    trpc.frontend.risk.scores.queryKey(),
    trpc.frontend.risk.riskScoresByRiskId.queryKey(),
    // Fast-path hint only: the risk_score upsert runs in an async event
    // consumer, so this races it — the 5s poll in
    // useGetLatestRiskScoresByRiskId is the convergence backstop.
    trpc.frontend.risk.latestRiskScoresByRiskId.queryKey(),
  ],
  mapResponse: (result: {
    Ids: string[];
  }): InsertRiskAssessmentResultsMutation => ({
    insertChildRiskAssessmentResult: {
      __typename: 'InsertChildRiskAssessmentResultOutput',
      Ids: result.Ids,
    },
  }),
  graphqlDocument: InsertRiskAssessmentResultsDocument,
  graphqlVariables: (input: InsertRiskAssessmentResultInput) => ({ ...input }),
  evictFields: [
    'risk_assessment_result',
    'assessment',
    'risk_assessment_result_aggregate',
  ],
  refetchQueries: [namedOperations.Query.getRiskScoresByRiskId],
});
